import { api } from './api';

/**
 * Web Push subscription flow.
 *
 * Steps the browser insists on, in order:
 *   1. Ask for Notification permission (produces the browser prompt).
 *   2. Register the service worker (or reuse the running one).
 *   3. Fetch the API's VAPID public key.
 *   4. Ask the SW to subscribe to Push, passing that key.
 *   5. Hand the resulting subscription (endpoint + keys) to the API so
 *      it can send us pushes later.
 *
 * All errors are surfaced as thrown Errors with human messages the
 * caller can show in an alert; no swallowed failures.
 */

interface PushKey { publicKey: string }

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator
      && 'PushManager' in window
      && 'Notification' in window;
}

export async function currentPushStatus(): Promise<'granted' | 'denied' | 'default' | 'unsupported'> {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

export async function enablePushNotifications(): Promise<'ok' | 'denied'> {
  if (!isPushSupported()) throw new Error('This browser does not support push notifications.');

  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return 'denied';

  const reg = await navigator.serviceWorker.ready;

  // If we already have a subscription on this browser, re-send it to the
  // API (it may have been dropped from the DB) rather than churn a fresh one.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { publicKey } = await api<PushKey>('/api/push/vapid-public-key');
    if (!publicKey) throw new Error('Push notifications are not configured on the server.');
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // Cast: DOM lib types `applicationServerKey` narrowly to ArrayBuffer,
      // but every browser accepts a Uint8Array here.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      applicationServerKey: urlBase64ToUint8Array(publicKey) as any,
    });
  }

  const raw = sub.toJSON() as {
    endpoint: string;
    keys?: { p256dh?: string; auth?: string };
  };
  if (!raw.endpoint || !raw.keys?.p256dh || !raw.keys?.auth) {
    throw new Error('The browser returned an incomplete push subscription.');
  }
  await api('/api/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({
      endpoint: raw.endpoint,
      keys: { p256dh: raw.keys.p256dh, auth: raw.keys.auth },
      userAgent: navigator.userAgent,
    }),
  });
  return 'ok';
}

export async function disablePushNotifications(): Promise<void> {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => { /* ignore */ });
  await api('/api/push/unsubscribe', {
    method: 'POST',
    body: JSON.stringify({ endpoint }),
  }).catch(() => { /* server rows are cleaned up on send-fail anyway */ });
}
